import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { APIError } from 'components/core/errors/api'
import { C1LabeledTextField } from 'components/core/RHKFormComponents/C1LabeledTextField'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import * as yup from 'yup'

export type PageHeaderEditTitleProps = {
    title?: string
    updateTitle: (title: string) => Promise<unknown>
    dialogTitle?: string | React.ReactElement
    dialogTextFieldLabel?: string | React.ReactElement
    dialogRequiredError?: string
    isRequired?: boolean
    multiline?: boolean
} & ClosableProps

export const PageHeaderEditTitle: FC<PageHeaderEditTitleProps> = ({
    title,
    updateTitle,
    close,
    dialogTitle = <FormattedMessage id="component_edit_dialog_title" defaultMessage="Update application name" />,
    dialogTextFieldLabel = <FormattedMessage id="component_edit_dialog_title" defaultMessage="Application name" />,
    dialogRequiredError,
    isRequired = true,
    multiline = false,
}): React.ReactElement => {
    const [formError, setFormError] = useState<RpcError | undefined>(undefined)
    const intl = useIntl()
    const schema = yup.object().shape({
        editTitle: isRequired
            ? yup.string().required(dialogRequiredError || intl.formatMessage({ id: 'field_error_value_required', defaultMessage: 'Title is required' }))
            : yup.string(),
    })

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    })

    const saveAndClose = async (field): Promise<void> => {
        setFormError(undefined)
        try {
            await updateTitle(field.editTitle)
        } catch (error) {
            if (error instanceof RpcError) {
                setFormError(error)
                return
            }

            setFormError(new RpcError(intl.formatMessage({
                id: 'component_edit_title_error_unknown',
                defaultMessage: 'An unknown error occurred. {err}',
            }, { err: JSON.stringify(error) })))
            return
        }
        close()
    }

    return (
        <C1Dialog maxWidth="xs" fullWidth open onClose={close}>
            <form onSubmit={handleSubmit(saveAndClose)}>
                <C1DialogTitle>
                    {dialogTitle}
                </C1DialogTitle>
                <C1DialogContent>
                    {formError && (
                        <Box mb={2}>
                            <APIError
                                err={formError}
                                title={
                                    <FormattedMessage
                                        id="component_edit_title_error"
                                        defaultMessage="Error: Failed to save"
                                    />
                                }
                            />
                        </Box>
                    )}

                    <C1LabeledTextField
                        register={register}
                        label={dialogTextFieldLabel}
                        name="editTitle"
                        defaultValue={title}
                        error={!!errors.editTitle}
                        helperText={errors.editTitle?.message}
                        fullWidth
                        autoFocus
                        multiline={multiline}
                    />
                </C1DialogContent>
                <C1DialogActions>
                    <C1Button onClick={close} variant="outlined">
                        <FormattedMessage id="component_edit_dialog_cancel" defaultMessage="Cancel" />
                    </C1Button>

                    <C1LoadingButton
                        type="submit"
                        color="primary"
                        variant="contained"
                        loading={false}
                    >
                        <FormattedMessage id="component_edit_dialog_save" defaultMessage="Update" />
                    </C1LoadingButton>
                </C1DialogActions>
            </form>
        </C1Dialog>
    )
}
